import { BlockBlobClient } from "@azure/storage-blob";

export const createUploadControl = () => {
  const abortController = new AbortController();
  let paused = false;
  let stopped = false;
  let resumeWaiters = [];

  const releaseWaiters = () => {
    resumeWaiters.forEach((resolve) => resolve());
    resumeWaiters = [];
  };

  return {
    pause: () => { paused = true; },
    resume: () => { paused = false; releaseWaiters(); },
    stop: () => {
      stopped = true;
      paused = false;
      abortController.abort();
      releaseWaiters();
    },
    waitWhilePaused: () => paused && !stopped
      ? new Promise((resolve) => resumeWaiters.push(resolve))
      : Promise.resolve(),
    get signal() { return abortController.signal; },
    get stopped() { return stopped; },
  };
};

export const uploadAudioFiles = async ({
  uploads,
  fileById,
  previous = [],
  control,
  onProgress,
  concurrency = 4,
}) => {
  const confirmations = [...previous];
  const completed = new Set(previous.map((item) => item.clientId));
  const active = new Set();
  const failed = [];
  let nextIndex = 0;

  const report = () => onProgress({
    done: completed.size,
    completed: [...completed],
    active: [...active],
    failed: [...failed],
  });
  report();

  const worker = async () => {
    while (!control.stopped) {
      await control.waitWhilePaused();
      if (control.stopped || nextIndex >= uploads.length) return;

      const upload = uploads[nextIndex++];
      const file = fileById.get(upload.clientId);
      if (!file) {
        failed.push({ clientId: upload.clientId, message: "Selected file is no longer available." });
        report();
        continue;
      }

      active.add(upload.clientId);
      report();
      try {
        const response = await new BlockBlobClient(upload.url).uploadBrowserData(file, {
          abortSignal: control.signal,
          blobHTTPHeaders: { blobContentType: "audio/mpeg" },
        });
        confirmations.push({
          clientId: upload.clientId,
          ...(response.etag ? { etag: response.etag } : {}),
        });
        completed.add(upload.clientId);
      } catch (error) {
        if (!control.stopped) {
          failed.push({ clientId: upload.clientId, message: error.message || "Azure upload failed." });
        }
      } finally {
        active.delete(upload.clientId);
        report();
      }
    }
  };

  await Promise.all(Array.from(
    { length: Math.min(concurrency, uploads.length) },
    worker,
  ));
  return { confirmations, failed, stopped: control.stopped };
};
